/**
 * Converter utility to transform Skyscanner API response to internal Kayak-compatible format
 * Converts HotelSearchContent from skyscanner-wrapper-api to SearchInPlaceRequestResponse
 */

import {
	HotelSearchContent,
	HotelResults,
	PricingOption,
	Agent,
	HotelInfo,
	HotelContent,
	StarsFilterValuesEnum,
	Price,
	SortOrder,
	SortType,
	SortItem,
	Sort,
} from '@/app/types/skyscanner-wrapper-api'
import {
	SearchInPlaceRequestResponse,
	SearchInPlaceHotel,
	SearchInPlaceHotelRate,
	SORT_BY,
	SORT_DIR,
} from '@/app/types'

/**
 * Maps internal SORT_BY enum to Skyscanner SortType
 */
export function mapSortByToSortType(by?: SORT_BY): SortType | undefined {
	if (!by) return undefined

	switch (by) {
		case SORT_BY.MIN_RATE:
			return SortType.SORT_ORDER_TYPE_PRICE
		case SORT_BY.CONSUMER_RATING:
		case SORT_BY.RATING:
			return SortType.SORT_ORDER_TYPE_HOTEL_GUEST_RATING
		case SORT_BY.STAR_RATING:
			return SortType.SORT_ORDER_TYPE_HOTEL_STAR_RATING
		case SORT_BY.DISTANCE:
			return SortType.SORT_ORDER_TYPE_HOTEL_PROXIMITY
		case SORT_BY.POPULARITY:
			return SortType.SORT_ORDER_TYPE_BEST
		default:
			return undefined
	}
}

/**
 * Maps internal SORT_DIR enum to Skyscanner SortOrder
 */
export function mapSortDirToSortOrder(dir?: SORT_DIR): SortOrder {
	switch (dir) {
		case SORT_DIR.ASC:
			return SortOrder.SORT_ORDER_DIRECTION_ASCENDING
		case SORT_DIR.DESC:
			return SortOrder.SORT_ORDER_DIRECTION_DESCENDING
		default:
			return SortOrder.SORT_ORDER_DIRECTION_UNSPECIFIED
	}
}

export function mapSortParamsToSort(by?: SORT_BY, dir?: SORT_DIR): Sort | undefined {
	const type = mapSortByToSortType(by)
	if (!type) return
	let order = mapSortDirToSortOrder(dir)
	if (
		type === SortType.SORT_ORDER_TYPE_BEST &&
		order === SortOrder.SORT_ORDER_DIRECTION_UNSPECIFIED
	) {
		order = SortOrder.SORT_ORDER_DIRECTION_DESCENDING
	}
	return { type, order }
}

/**
 * Maps Skyscanner star rating enum to numeric value (1-5)
 */
function mapStarsToNumber(stars?: StarsFilterValuesEnum): 1 | 2 | 3 | 4 | 5 {
	switch (stars) {
		case 'STARS_ONE_STAR':
			return 1
		case 'STARS_TWO_STAR':
			return 2
		case 'STARS_THREE_STAR':
			return 3
		case 'STARS_FOUR_STAR':
			return 4
		case 'STARS_FIVE_STAR':
			return 5
		default:
			return 3 // Default to 3 stars if unspecified
	}
}

/**
 * Derives a guest rating (0-10 scale) from star rating (1-5 scale)
 * Uses a reasonable mapping where higher star ratings correlate with higher guest ratings
 */
function deriveGuestRatingFromStars(stars?: StarsFilterValuesEnum): number {
	const starRating = mapStarsToNumber(stars)

	// Map star ratings to typical guest rating ranges
	// 1 star -> 5.0-6.0 range
	// 2 star -> 6.0-7.0 range
	// 3 star -> 7.0-8.0 range
	// 4 star -> 8.0-9.0 range
	// 5 star -> 9.0-9.5 range
	const baseRating = 4 + starRating
	const variance = 0.5

	// Add slight variance to make it more realistic (mid-point of range)
	return baseRating + variance
}

/**
 * Gets guest rating from hotelContent, preferring actual guestRating over derived star rating
 * Converts Skyscanner's 1-5 scale to 0-10 scale
 */
function getGuestRating(hotelContent?: HotelContent): number {
	// Use actual guest rating if available (convert from 1-5 to 0-10 scale)
	if (hotelContent?.guestRating?.score) {
		return hotelContent.guestRating.score * 2
	}

	// Fallback: derive from star rating
	return deriveGuestRatingFromStars(hotelContent?.stars)
}

/**
 * Formats logo url
 * Sometimes logos are relative
 */
function formatLogoUrl(agentLogoUrl?: string) {
	if (!agentLogoUrl) return ''
	return `${URL.canParse(agentLogoUrl) ? '' : IMAGE_BASE_URL}${agentLogoUrl}`
}

/**
 * Converts Skyscanner pricing option to internal hotel rate format
 */
function convertPricingOptionToRate(
	pricingOption: PricingOption,
	nights: number,
	agent?: Agent
): SearchInPlaceHotelRate {
	const totalRate = pricingOption.price?.price || 0
	const nightlyRate = totalRate / nights
	const logo = formatLogoUrl(agent?.logoUrl)

	return {
		totalRate: totalRate.toString(),
		nightlyRate: nightlyRate.toString(),
		providerName: agent?.name || 'Unknown',
		bookURI: pricingOption.deeplink || '',
		hasFreeCancellation:
			pricingOption.cancellationPolicy ===
			'CANCELLATION_POLICY_TYPE_FREE_CANCELLATION',
		canPayLater: pricingOption.paymentType === 'PAYMENT_TYPE_PAY_ON_ARRIVAL',
		rawPrice: totalRate,
		provider: {
			name: agent?.name || 'Unknown',
			logo,
		},
	}
}

/**
 * Converts Skyscanner hotel data to internal SearchInPlaceHotel format
 */
function convertToSearchInPlaceHotel(
	hotelId: string,
	pricingOptions: PricingOption[],
	nights: number,
	hotelContent?: HotelContent,
	hotelInfo?: HotelInfo,
	agents?: Record<string, Agent>
): SearchInPlaceHotel {
	// Find the best (cheapest) rate
	const sortedPricing = [...pricingOptions].sort(
		(a, b) => (a.price?.price || 0) - (b.price?.price || 0)
	)
	const bestPricingOption = sortedPricing[0]
	const bestAgent = bestPricingOption?.agentId
		? agents?.[bestPricingOption.agentId]
		: undefined

	// Convert all pricing options to rates
	const rates: SearchInPlaceHotelRate[] = pricingOptions.map((option) => {
		const agent = option.agentId ? agents?.[option.agentId] : undefined
		return convertPricingOptionToRate(option, nights, agent)
	})

	// Get best rate details
	const bestRate = rates[0] || {
		totalRate: '0',
		nightlyRate: '0',
		providerName: 'Unknown',
		bookURI: '',
		hasFreeCancellation: false,
		canPayLater: false,
		rawPrice: 0,
		provider: {
			name: 'Unknown',
			logo: '',
		},
	}

	// Extract images
	const images = hotelContent?.hotelImages || []
	const mainImage =
		images.find((img) => img.type === 'IMAGE_TYPE_OUTLOOK') || images[0]

	return {
		id: hotelId,
		key: `khotel:${hotelId}`, // TODO: remove 'khotel'
		address: '', // Not available in Skyscanner API
		// TODO: Respect distanceFromTarget.unitCode when converting distance
		distance: hotelInfo?.distanceFromTarget?.value
			? hotelInfo.distanceFromTarget.value //  * 1000
			: 0, // Convert km to meters
		numberOfReviews: hotelContent?.guestRating?.reviewCount || 0,
		propertyType: 0, // Would need mapping from accommodationType
		// TODO: Just place bestRate here?
		bestRate: {
			// TODO: remove unavailable fields here and from UI
			availableRooms: 1, // Not available in Skyscanner API
			bookURI: bestPricingOption?.deeplink || '',
			canPayLater:
				bestPricingOption?.paymentType === 'PAYMENT_TYPE_PAY_ON_ARRIVAL',
			hasFreeCancellation:
				bestPricingOption?.cancellationPolicy ===
				'CANCELLATION_POLICY_TYPE_FREE_CANCELLATION',
			inclusions: [0], // Not available in Skyscanner API
			isCheapestRate: true,
			providerIndex: 0,
			roomName: bestPricingOption?.roomName || '',
			totalRate: bestPricingOption?.price?.price || 0,
			nightlyRate: (bestPricingOption?.price?.price || 0) / nights,
			provider: {
				name: bestAgent?.name || 'Unknown',
				logo: formatLogoUrl(bestAgent?.logoUrl),
			},
		},
		guestRating: getGuestRating(hotelContent),
		guestRatingSentiment: '', // Not available in Skyscanner API
		image: mainImage?.thumbnailUrl || '',
		largeImage: mainImage?.fullUrl || mainImage?.galleryUrl || '',
		lat: hotelContent?.coordinates?.latitude,
		long: hotelContent?.coordinates?.longitude,
		mainImage: mainImage
			? {
					small: mainImage.thumbnailUrl || '',
					large: mainImage.fullUrl || mainImage.galleryUrl || '',
			  }
			: undefined,
		name: hotelContent?.hotelName || '',
		starRating: mapStarsToNumber(hotelContent?.stars),
		rates,
		moreImages: images.map((img) => ({
			small: img.thumbnailUrl || '',
			large: img.fullUrl || img.galleryUrl || '',
		})),
	}
}

/**
 * Calculates price range from all pricing options
 */
function calculatePriceRange(
	hotelsPricingOptions?: Record<string, PricingOption>,
	nights = 1
): {
	lowestTotalRate: number
	highestTotalRate: number
	lowestNightlyRate: number
	highestNightlyRate: number
} {
	if (!hotelsPricingOptions || Object.keys(hotelsPricingOptions).length === 0) {
		return {
			lowestTotalRate: 0,
			highestTotalRate: 0,
			lowestNightlyRate: 0,
			highestNightlyRate: 0,
		}
	}

	const prices = Object.values(hotelsPricingOptions)
		.map((option) => ({
			total: option.price?.price || 0,
			nightly: (option.price?.price || 0) / nights,
		}))
		.filter((p) => p.total > 0)

	if (prices.length === 0) {
		return {
			lowestTotalRate: 0,
			highestTotalRate: 0,
			lowestNightlyRate: 0,
			highestNightlyRate: 0,
		}
	}

	return {
		lowestTotalRate: Math.min(...prices.map((p) => p.total)),
		highestTotalRate: Math.max(...prices.map((p) => p.total)),
		lowestNightlyRate: Math.min(...prices.map((p) => p.nightly)),
		highestNightlyRate: Math.max(...prices.map((p) => p.nightly)),
	}
}

/**
 * Groups pricing options by hotel ID
 */
function groupPricingOptionsByHotel(
	hotelsPricingOptions?: Record<string, PricingOption>
): Record<string, PricingOption[]> {
	if (!hotelsPricingOptions) {
		return {}
	}

	const grouped: Record<string, PricingOption[]> = {}

	Object.values(hotelsPricingOptions).forEach((option) => {
		const hotelId = option.hotelId
		if (!hotelId) return

		if (!grouped[hotelId]) {
			grouped[hotelId] = []
		}
		grouped[hotelId].push(option)
	})

	return grouped
}

/**
 * Sort hotel by sortOptoins
 */
function sortHotels(
	hotels: SearchInPlaceHotel[],
	sortItems: SortItem[] | undefined
) {
	if (!sortItems || !sortItems.length) return
	// Create a map of hotelId to sort index for efficient lookup
	const sortOrderMap = new Map<string, number>()
	sortItems
		.sort((a, b) => (b.value || 0) - (a.value || 0))
		.forEach((item, index) => {
			if (item.itemId) {
				sortOrderMap.set(item.itemId, index)
			}
		})

	hotels.sort(
		(a, b) => (sortOrderMap.get(a.id) || 0) - (sortOrderMap.get(b.id) || 0)
	)
}

/**
 * Main converter function: transforms HotelSearchContent to SearchInPlaceRequestResponse
 *
 * @param content - Skyscanner HotelSearchContent response
 * @param locale - Locale string (e.g., "en-GB")
 * @param currency - Currency code (e.g., "USD")
 * @param page - Current page number (default: 0)
 * @param perPage - Results per page (default: 25)
 * @param complete - Whether the search is complete (default: false)
 * @returns SearchInPlaceRequestResponse compatible with internal format
 */

// TODO: move to env
// Also try https://content.skyscnr.com
const IMAGE_BASE_URL = 'https://www.skyscanner.net'

export function convertSkyscannerToKayakFormat(
	content: HotelSearchContent,
	nights = 1,
	locale: string = 'en-US',
	currency: string = 'USD',
	page: number = 0,
	perPage: number = 25,
	complete: boolean = true
): SearchInPlaceRequestResponse {
	const results = content.results || {}
	const {
		hotelsPricingOptions,
		agents,
		hotelInfo: hotelInfoMap,
		hotelContent: hotelContentMap,
	} = results

	// Group pricing options by hotel
	const groupedPricing = groupPricingOptionsByHotel(hotelsPricingOptions)

	// Convert each hotel
	const hotels: SearchInPlaceHotel[] = Object.entries(groupedPricing).map(
		([hotelId, pricingOptions]) => {
			return convertToSearchInPlaceHotel(
				hotelId,
				pricingOptions,
				nights,
				hotelContentMap?.[hotelId],
				hotelInfoMap?.[hotelId],
				agents
			)
		}
	)

	sortHotels(hotels, results.sortOptions?.hotel?.sortItems)

	// Calculate price ranges
	const priceRange = calculatePriceRange(hotelsPricingOptions, nights)

	// Extract language and country code from locale
	const [languageCode, countryCode] = locale.split('-')

	// Calculate pagination
	const totalResults = content.totalHotelResultCount || hotels.length
	const pages = Math.ceil(totalResults / perPage)

	return {
		countryCode: countryCode || 'US',
		currencyCode: currency,
		hotels,
		languageCode: languageCode || 'en',
		page,
		pages,
		perPage,
		totalResults,
		complete,
		pullTimeout: 0,
		lowestTotalRate: priceRange.lowestTotalRate,
		highestTotalRate: priceRange.highestTotalRate,
		lowestNightlyRate: priceRange.lowestNightlyRate,
		highestNightlyRate: priceRange.highestNightlyRate,
		// Filters are not available in Skyscanner API response
		// Would need to be calculated from the data if needed
	}
}

/**
 * Helper function to extract locale from HotelSearchQuery if available
 */
export function extractLocaleFromQuery(query?: {
	locale?: string
	market?: string
}): string {
	if (query?.locale) {
		return query.locale
	}
	if (query?.market) {
		return `en-${query.market}`
	}
	return 'en-US'
}

/**
 * Helper function to extract currency from HotelSearchQuery if available
 */
export function extractCurrencyFromQuery(query?: {
	currency?: string
}): string {
	return query?.currency || 'USD'
}
